import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, WarningCircle, Warning, Info, Pulse, Heart, Drop, Thermometer, Wind, Flame, User, ArrowsClockwise } from "@phosphor-icons/react";
import { RecoveryChart, HrvRhrChart, SleepStagesChart, SleepQualityChart, StrainRecoveryChart, CaloriesChart, SleepTimesChart } from "./Charts";
import WorkoutsList from "./WorkoutsList";
import { StrainCoachCard, SleepCoachCard, VerdictCard, HealthMonitorCard } from "./CoachCards";
import { PeriodCompareCard, CorrelationsCard, RecordsCard } from "./ExtrasCards";
import AICoachCard from "./AICoachCard";
import WeeklyDigestCard from "./WeeklyDigestCard";
import AIChatCard from "./AIChatCard";
import GoalsCard from "./GoalsCard";
import PushCard from "./PushCard";
import JournalTab from "./JournalTab";
import CustomizeCards from "./CustomizeCards";
import AcwrCard from "./AcwrCard";
import PolarityCard from "./PolarityCard";
import WellbeingCard from "./WellbeingCard";
import ForecastCard from "./ForecastCard";
import PredictorCard from "./PredictorCard";
import IllnessCard from "./IllnessCard";
import SleepWindowCard from "./SleepWindowCard";
import SleepBankCard from "./SleepBankCard";
import CircadianCard from "./CircadianCard";
import WeekSimulator from "./WeekSimulator";
import RecoveryAfterLoadCard from "./RecoveryAfterLoadCard";
import SportAnalysis from "./SportAnalysis";
import PatternsTab from "./PatternsTab";
import TrendsTab from "./TrendsTab";
import MetricDetailSheet from "./MetricDetailSheet";
import DayDetailSheet from "./DayDetailSheet";
import DayStoryCard from "./DayStoryCard";
import WorkoutLogCard from "./WorkoutLogCard";
import { metricStats, trendGood, aggregateMonthly } from "./metricDefs";
import SectionGroup from "./SectionGroup";
import DayFocusCard from "./DayFocusCard";
import { InfoHint } from "./InfoHint";
import { TrendUp, TrendDown } from "@phosphor-icons/react";

function Ring({ value, max = 100, color, label, unit }) {
  const pct = value != null ? Math.min((value / max) * 100, 100) : 0;
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="hsl(220 10% 15%)" strokeWidth="8" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-2xl font-bold num">{value != null ? Math.round(value * 10) / 10 : "—"}</span>
          {unit && <span className="text-[10px] text-muted-foreground">{unit}</span>}
        </div>
      </div>
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
    </div>
  );
}

const insightIcon = {
  success: <CheckCircle size={20} weight="fill" className="text-[#00e676] shrink-0" />,
  warning: <Warning size={20} weight="fill" className="text-[#ffea00] shrink-0" />,
  alert: <WarningCircle size={20} weight="fill" className="text-[#ff3b30] shrink-0" />,
  info: <Info size={20} weight="fill" className="text-primary shrink-0" />,
};
const insightBorder = {
  success: "border-[#00e676]/25",
  warning: "border-[#ffea00]/25",
  alert: "border-[#ff3b30]/25",
  info: "border-primary/25",
};

const recoveryColor = (v) => (v == null ? "#555" : v >= 67 ? "#00e676" : v >= 34 ? "#ffea00" : "#ff3b30");

export default function HealthAnalytics({ member = "" }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [metricKey, setMetricKey] = useState(null);
  const [dayDate, setDayDate] = useState(null);
  const [monthly, setMonthly] = useState(true);
  const [prefs, setPrefs] = useState({ order: [], hidden: [] });

  useEffect(() => {
    if (member) return;
    api.get("/prefs/health").then((r) => setPrefs(r.data)).catch(() => {});
  }, [member]);

  const fetchData = (force = false) => {
    setLoading(true);
    api.get(`/whoop/analytics?days=${days}${force ? "&force=true" : ""}${member ? `&member=${member}` : ""}`)
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Не удалось загрузить данные"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [days]); // eslint-disable-line

  if (loading && !data) {
    return <div className="text-sm text-muted-foreground p-6" data-testid="analytics-loading">Загружаем данные Whoop…</div>;
  }
  if (!data) {
    return err ? (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground" data-testid="analytics-error">
        {member ? "Данные недоступны — участник ещё не подключил Whoop." : err}
      </div>
    ) : null;
  }

  const { today, averages, insights, workouts, profile, body, coach, verdict, health_monitor, correlations, records, period_compare, period_days } = data;
  const daysData = data.days;
  const isAllTime = days === 0;
  const useMonthly = isAllTime && monthly;
  const hiddenIds = member ? ["day_focus", "ai_coach", "digest", "chat", "goals_push", "workouts_log"] : prefs.hidden;
  const chartData = useMonthly ? aggregateMonthly(daysData) : daysData;
  const chartDayClick = useMonthly ? undefined : setDayDate;

  const SECTION_META = [
    { id: "day_focus", label: "Фокус дня" },
    { id: "ai_coach", label: "AI-тренер · разбор дня" },
    { id: "digest", label: "AI-дайджест недели" },
    { id: "chat", label: "Чат с AI-тренером" },
    { id: "rings", label: "Кольца и показатели дня" },
    { id: "day_story", label: "История дня" },
    { id: "workouts_log", label: "Мои тренировки и упражнения" },
    { id: "coach_row", label: "Коуч: нагрузка · сон · вердикт" },
    { id: "compare", label: "Сравнение периодов" },
    { id: "forecast", label: "Прогноз и детектор болезни" },
    { id: "predictor", label: "Симулятор «что если»" },
    { id: "monitor", label: "Монитор здоровья" },
    { id: "corr_records", label: "Корреляции и рекорды" },
    { id: "insights", label: "Советы" },
    { id: "goals_push", label: "Цели и уведомления" },
  ];
  const GROUPS = [
    { id: "today", title: "Сегодня", desc: "Восстановление, нагрузка, сон и хроника вашего дня", sections: ["rings", "day_story", "workouts_log", "coach_row"], defaultOpen: true },
    { id: "ai", title: "AI-тренер", desc: "Персональный разбор дня, недельный дайджест и чат с тренером", sections: ["ai_coach", "digest", "chat"] },
    { id: "analytics", title: "Глубокая аналитика", desc: "Сравнение периодов, прогнозы, монитор здоровья, корреляции и рекорды", sections: ["compare", "forecast", "predictor", "monitor", "corr_records"] },
    { id: "advice", title: "Советы, цели и уведомления", desc: "Рекомендации по вашим данным, цели и push-уведомления", sections: ["insights", "goals_push"] },
  ];

  const effectiveOrder = [
    ...prefs.order.filter((id) => SECTION_META.some((s) => s.id === id)),
    ...SECTION_META.map((s) => s.id).filter((id) => !prefs.order.includes(id)),
  ];
  const savePrefs = (order, hidden) => {
    setPrefs({ order, hidden });
    api.put("/prefs/health", { order, hidden }).catch(() => {});
  };

  const metric = (icon, label, key, value, unit) => {
    const s = metricStats(daysData, key);
    const pct = s?.avg ? ((s.current - s.avg) / Math.abs(s.avg)) * 100 : null;
    const dir = pct != null ? trendGood(key, pct) : "neutral";
    return (
      <button onClick={() => setMetricKey(key)} data-testid={`metric-row-${key}`}
        className="w-full flex items-center gap-2.5 py-2 rounded-md hover:bg-secondary/50 transition-colors text-left px-1 -mx-1">
        {icon}
        <span className="text-xs text-muted-foreground flex-1">{label}</span>
        {pct != null && Math.abs(pct) >= 3 && (
          <span className={`flex items-center text-[10px] num ${dir === "good" ? "text-[#00e676]" : dir === "bad" ? "text-[#ff3b30]" : "text-muted-foreground"}`}>
            {pct > 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}{Math.abs(pct).toFixed(0)}%
          </span>
        )}
        <span className="text-sm num font-semibold">{value != null ? value : "—"}{value != null && unit ? ` ${unit}` : ""}</span>
      </button>
    );
  };

  const header = (
      <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
        {[7, 14, 30, 90, 0].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={d === 0 ? "period-all" : `period-${d}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${days === d ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {d === 0 ? "Всё время" : `${d} дней`}
          </button>
        ))}
        <button onClick={() => fetchData(true)} disabled={loading} data-testid="refresh-button"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Обновить
        </button>
        <CustomizeCards sections={SECTION_META} order={effectiveOrder} hidden={prefs.hidden} onChange={savePrefs} hide={!!member} />
        {isAllTime && (
          <div className="flex items-center gap-1 rounded-md bg-secondary p-0.5" data-testid="agg-toggle">
            <button onClick={() => setMonthly(true)} data-testid="agg-monthly"
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${monthly ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
              По месяцам
            </button>
            <button onClick={() => setMonthly(false)} data-testid="agg-daily"
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${!monthly ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
              По дням
            </button>
          </div>
        )}
        {data.fetched_at && (
          <span className="text-xs text-muted-foreground ml-auto" data-testid="last-updated">
            Обновлено {new Date(data.fetched_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} · автообновление каждые 5 мин
          </span>
        )}
      </div>
  );

  const renderSection = (id) => {
    switch (id) {
      case "ai_coach": return <AICoachCard />;
      case "digest": return <WeeklyDigestCard />;
      case "chat": return <AIChatCard />;
      case "rings": return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <button onClick={() => setMetricKey("recovery")} className="glass-card p-4 sm:p-6 flex justify-center cursor-pointer hover:border-primary/40 transition-colors" data-testid="ring-recovery">
          <Ring value={today.recovery} color={recoveryColor(today.recovery)} label="Восстановление" unit="%" />
        </button>
        <button onClick={() => setMetricKey("strain")} className="glass-card p-4 sm:p-6 flex justify-center cursor-pointer hover:border-primary/40 transition-colors" data-testid="ring-strain">
          <Ring value={today.strain} max={21} color="#0084ff" label="Нагрузка" unit="из 21" />
        </button>
        <button onClick={() => setMetricKey("sleep_performance")} className="col-span-2 md:col-span-1 glass-card p-4 sm:p-6 flex justify-center cursor-pointer hover:border-primary/40 transition-colors" data-testid="ring-sleep">
          <Ring value={today.sleep_performance} color="#a970ff" label="Сон" unit="%" />
        </button>
        <div className="col-span-2 md:col-span-1 glass-card px-4 sm:px-6 py-4" data-testid="today-metrics">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Показатели · нажмите для деталей</div>
          {metric(<Pulse size={16} className="text-primary" />, "HRV", "hrv", today.hrv, "мс")}
          {metric(<Heart size={16} className="text-[#ff3b30]" />, "Пульс покоя", "rhr", today.rhr, "уд/мин")}
          {metric(<Drop size={16} className="text-[#4dabf5]" />, "SpO₂", "spo2", today.spo2, "%")}
          {metric(<Thermometer size={16} className="text-[#ff9100]" />, "Темп. кожи", "skin_temp", today.skin_temp, "°C")}
          {metric(<Wind size={16} className="text-[#a970ff]" />, "Дыхание", "resp_rate", today.resp_rate, "вд/мин")}
          {metric(<Flame size={16} className="text-[#ffea00]" />, "Калории", "kcal", today.kcal, "ккал")}
        </div>
        <div className="col-span-2 md:col-span-1 glass-card px-4 sm:px-6 py-4" data-testid="profile-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Профиль Whoop</div>
          <div className="flex items-center gap-2 py-2">
            <User size={16} className="text-primary" />
            <span className="text-sm font-semibold truncate">{profile.first_name} {profile.last_name}</span>
          </div>
          {[["Рост", body.height_cm, "см"], ["Вес", body.weight_kg, "кг"], ["Макс. пульс", body.max_heart_rate, "уд/мин"]].map(([l, v, u]) => (
            <div key={l} className="flex items-center gap-2.5 py-2 px-1">
              <span className="w-4" />
              <span className="text-xs text-muted-foreground flex-1">{l}</span>
              <span className="text-sm num font-semibold">{v != null ? `${v} ${u}` : "—"}</span>
            </div>
          ))}
          <div className="pt-2 mt-1 border-t border-border/40 text-xs text-muted-foreground">
            Средние {days === 0 ? "за всё время" : `за ${days} дн.`}: восст. <span className="num text-foreground">{averages.recovery ?? "—"}%</span>, сон <span className="num text-foreground">{averages.sleep_h ?? "—"} ч</span>, нагрузка <span className="num text-foreground">{averages.strain ?? "—"}</span>
          </div>
        </div>
      </div>
      );
      case "day_story": return <DayStoryCard member={member} />;
      case "workouts_log": return <WorkoutLogCard />;
      case "coach_row": return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="coach-row">
        <div onClick={() => setMetricKey("strain")} className="cursor-pointer">
          <StrainCoachCard strain={today.strain} target={coach?.strain_target} verdict={coach?.strain_verdict} />
        </div>
        <div onClick={() => setMetricKey("sleep_h")} className="cursor-pointer">
          <SleepCoachCard sleep={coach?.sleep} />
        </div>
        <div onClick={() => setMetricKey("recovery")} className="cursor-pointer">
          <VerdictCard verdict={verdict} />
        </div>
      </div>
      );
      case "compare": return <PeriodCompareCard compare={period_compare} periodDays={period_days} onSelect={setMetricKey} />;
      case "forecast": return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ForecastCard forecast={data.forecast} />
        <IllnessCard risk={data.illness_risk} />
      </div>
      );
      case "predictor": return <PredictorCard predictor={data.predictor} />;
      case "monitor": return <HealthMonitorCard monitor={health_monitor} onSelect={setMetricKey} />;
      case "corr_records": return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CorrelationsCard correlations={correlations} onSelect={setMetricKey} />
        <RecordsCard records={records} onSelectDay={setDayDate} />
      </div>
      );
      case "insights": return (
      <div data-testid="insights-section">
        <h2 className="font-heading font-semibold text-lg mb-3">Советы <span className="text-xs font-normal text-muted-foreground">· нажмите, чтобы открыть показатель</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {insights.map((ins, i) => (
            <button key={i} onClick={() => ins.metric && setMetricKey(ins.metric)}
              className={`glass-card p-4 flex gap-3 text-left transition-colors ${insightBorder[ins.level]} ${ins.metric ? "hover:bg-secondary/40 cursor-pointer" : "cursor-default"}`}
              data-testid={`insight-${i}`}>
              {insightIcon[ins.level]}
              <div>
                <div className="text-sm font-semibold mb-1">{ins.title}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{ins.text}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      );
      case "goals_push": return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GoalsCard />
        <PushCard />
      </div>
      );
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="whoop-analytics">
      {header}

      {!hiddenIds.includes("day_focus") && (
        <DayFocusCard today={today} coach={coach} acwr={data.acwr} illness={data.illness_risk} />
      )}

      {GROUPS.map((g) => {
        const vis = effectiveOrder.filter((id) => g.sections.includes(id) && !hiddenIds.includes(id));
        if (vis.length === 0) return null;
        return (
          <SectionGroup key={g.id} id={g.id} title={g.title} desc={g.desc} defaultOpen={g.defaultOpen} count={vis.length}>
            {vis.map((id) => (
              <div key={id}>{renderSection(id)}</div>
            ))}
          </SectionGroup>
        );
      })}

      <Tabs defaultValue="recovery" data-testid="analytics-tabs">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="glass-panel border border-white/[0.07] rounded-xl w-max h-auto p-1">
            <TabsTrigger value="recovery" data-testid="tab-recovery">Восстановление</TabsTrigger>
            <TabsTrigger value="sleep" data-testid="tab-sleep">Сон</TabsTrigger>
            <TabsTrigger value="strain" data-testid="tab-strain">Нагрузка</TabsTrigger>
            <TabsTrigger value="workouts" data-testid="tab-workouts">Тренировки</TabsTrigger>
            <TabsTrigger value="patterns" data-testid="tab-patterns">Паттерны</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">Тренды</TabsTrigger>
            {!member && <TabsTrigger value="journal" data-testid="tab-journal">Журнал</TabsTrigger>}
          </TabsList>
        </div>
        <TabsContent value="recovery" className="space-y-4 mt-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-recovery">Динамика восстановления, HRV и пульса покоя — насколько организм готов к нагрузкам.</p>
          {!member && <WellbeingCard days={daysData} />}
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">Восстановление {useMonthly ? "по месяцам (средние)" : "по дням"} {!useMonthly && <span className="text-xs font-normal text-muted-foreground">· клик по дню — детали</span>}</h3>
            <RecoveryChart data={chartData} onDayClick={chartDayClick} norm={data.norms?.recovery} />
          </div>
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">HRV и пульс покоя</h3>
            <HrvRhrChart data={chartData} onDayClick={chartDayClick} normHrv={data.norms?.hrv} />
          </div>
        </TabsContent>
        <TabsContent value="sleep" className="space-y-4 mt-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-sleep">Фазы и качество сна, режим отбоя и подъёма, банк сна и циркадный ритм.</p>
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">Фазы сна (часы{useMonthly ? ", средние по месяцам" : ""}) {!useMonthly && <span className="text-xs font-normal text-muted-foreground">· клик по дню — детали</span>}</h3>
            <SleepStagesChart data={chartData} onDayClick={chartDayClick} />
          </div>
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">Качество сна</h3>
            <SleepQualityChart data={chartData} onDayClick={chartDayClick} />
          </div>
          <div className="glass-card p-5" data-testid="sleep-times-chart">
            <h3 className="text-sm font-semibold mb-3">Режим сна: отбой → подъём <span className="text-xs font-normal text-muted-foreground">· клик по дню — детали</span></h3>
            <SleepTimesChart data={daysData} onDayClick={setDayDate} />
          </div>
          <SleepBankCard bank={data.sleep_bank} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SleepWindowCard windowData={data.sleep_window} />
            <CircadianCard circ={data.circadian} />
          </div>
        </TabsContent>
        <TabsContent value="strain" className="space-y-4 mt-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-strain">Тренировочная нагрузка, риск перетренированности и затраты энергии.</p>
          <AcwrCard acwr={data.acwr} />
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">Нагрузка vs Восстановление {!useMonthly && <span className="text-xs font-normal text-muted-foreground">· клик по дню — детали</span>}</h3>
            <StrainRecoveryChart data={chartData} onDayClick={chartDayClick} />
          </div>
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-3">Затраты энергии</h3>
            <CaloriesChart data={chartData} onDayClick={chartDayClick} />
          </div>
          <WeekSimulator forecast={data.forecast} />
          <RecoveryAfterLoadCard rows={data.workout_recovery} />
        </TabsContent>
        <TabsContent value="workouts" className="space-y-4 mt-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-workouts">Все тренировки, анализ по видам спорта и распределение интенсивности.</p>
          <SportAnalysis sports={data.sport_analysis} />
          <PolarityCard polarity={data.polarity} />
          <WorkoutsList workouts={workouts} maxHr={body.max_heart_rate} />
        </TabsContent>
        <TabsContent value="patterns" className="mt-4 space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-patterns">Автоматически найденные закономерности в ваших данных: что влияет на восстановление и сон.</p>
          <PatternsTab member={member} />
        </TabsContent>

        <TabsContent value="trends" className="mt-4 space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-trends">Год одним взглядом, динамика месяца, баланс нагрузки и что влияет именно на вас.</p>
          <TrendsTab member={member} />
        </TabsContent>

        {!member && (
        <TabsContent value="journal" className="mt-4 space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="tab-desc-journal">Дневник привычек и самочувствия — и его связь с показателями Whoop.</p>
          <JournalTab />
        </TabsContent>
        )}
      </Tabs>

      <MetricDetailSheet metricKey={metricKey} days={daysData} onClose={() => setMetricKey(null)} />
      <DayDetailSheet date={dayDate} days={daysData} workouts={workouts} onClose={() => setDayDate(null)} />
    </div>
  );
}
